import {
  BodyText,
  Box,
  Card,
  Combobox,
  ComboboxEmpty,
  ComboboxItem,
  Flex,
  useComboboxFilter,
} from '@utilitywarehouse/hearth-react';
import { useEffect, useRef } from 'react';
import { memo, useDeferredValue, useMemo } from 'react';

import { BrandLogo } from '@features/Cashback/Calculator/BrandLogo/BrandLogo';
import { RetailPartner } from '@generated/gql/graphql';

import './styles.scss';

interface BrandSearchResultsProps {
  brands: RetailPartner[];
  searchQuery: string;
}

const BrandSearchResults = memo(
  ({ brands, searchQuery }: BrandSearchResultsProps) => {
    const { contains } = useComboboxFilter();
    const filteredBrands = useMemo(() => {
      return brands
        .filter((brand) => contains(brand, searchQuery, (brand) => brand.name))
        .sort((a, b) => a.name.localeCompare(b.name));
    }, [searchQuery, contains, brands]);

    return (
      <>
        {filteredBrands.length === 0 && (
          <ComboboxEmpty>Retailer not found</ComboboxEmpty>
        )}
        {filteredBrands.map((brand) => (
          <ComboboxItem
            value={brand.id}
            key={brand.id}
            className="brandSearch__Item"
          >
            <Flex spacing="md" alignItems="center">
              <Card paddingNone variant="emphasis" colorScheme="neutralStrong">
                <BrandLogo brand={brand} />
              </Card>
              <BodyText
                weight="semibold"
                size="lg"
                className="brandSearch__ItemLabel"
              >
                {brand.name}
              </BodyText>
              <BodyText weight="semibold" size="lg">
                Add +
              </BodyText>
            </Flex>
          </ComboboxItem>
        ))}
      </>
    );
  }
);

interface BrandSearchProps {
  brands: RetailPartner[];
  inputValue?: string;
  disabled?: boolean;
  loading?: boolean;
  onChange: (value: string) => void;
  onSelect: (value: Nullable<string>) => void;
}

const BrandSearch = ({
  brands,
  onChange,
  onSelect,
  inputValue = '',
  disabled = false,
  loading = false,
}: BrandSearchProps) => {
  // using a deferred value, we can wait to update the search results
  // until a later render cycle, which should prevent UI blocking
  // in case of expensive operations. Notable when there are many
  // retailers in the search list, such as when first entering a
  // serach query
  // https://react.dev/reference/react/useDeferredValue#deferring-re-rendering-for-a-part-of-the-ui
  const deferredInput = useDeferredValue(inputValue);

  const retailerComboboxWrapperRef = useRef<HTMLDivElement>(null);
  // since the combobox results opens in a portal, and it uses a BaseUi
  // variable tied to the input element's width to determine the result
  // pop-up width, we need to create a new variable based on the InputBase
  // dimensions. This ensures the results size instead fills the composite
  // component dimensions instead.
  // This requires a style override, which is found in ./styles.scss
  useEffect(() => {
    const wrapper = retailerComboboxWrapperRef.current;
    const inputBase = wrapper?.querySelector<HTMLElement>('.h-InputBase');
    if (!inputBase) return;

    const observer = new ResizeObserver(([entry]) => {
      document.documentElement.style.setProperty(
        '--retailer-combobox-width',
        `${entry.borderBoxSize?.[0]?.inlineSize ?? entry.contentRect.width}px`
      );
    });
    observer.observe(inputBase);
    return () => observer.disconnect();
  }, []);

  return (
    <Box ref={retailerComboboxWrapperRef}>
      <Combobox
        onValueChange={(value: Nullable<string>) => onSelect(value)}
        onInputValueChange={(value) => onChange(value)}
        inputValue={inputValue}
        label="Add brand"
        placeholder="Search for brand"
        value={null}
        disabled={disabled}
        loading={loading}
        className="brandSearch__Search"
        required
      >
        <BrandSearchResults brands={brands} searchQuery={deferredInput} />
      </Combobox>
    </Box>
  );
};

export { BrandSearch, type BrandSearchProps };
